import { type ReactNode, useEffect, useState } from "react";
import Image from "next/image";
import CheckIcon from "@/assets/svgs/check.svg";
import CloseIcon from "@/assets/svgs/close.svg";
import TruckIcon from "@/assets/svgs/truck.svg";
import { FreeShippingProgress } from "@/components/shared/data-display/free-shipping-progress/free-shipping-progress";
import { Button } from "@/components/shared/inputs/button/button";
import { IconButton } from "@/components/shared/inputs/icon-button/icon-button";
import { Drawer } from "@/components/shared/surfaces/drawer/drawer";
import { noImagePlaceholderLarge } from "@/types/shared.types";
import type {
  LegacyCartItem,
  LegacyCartResponse,
} from "@/types/legacy-cart.types";
import type { SliderRecommendations } from "@/types/slider.types";
import { logError } from "@/infrastructure/observability/error-logging.service";
import { getWorkDrawerRecentBrowsingSlider } from "@/services/work.service";
import { DEFAULT_FREE_SHIPPING_THRESHOLD } from "@/utility/constants";
import { formatUsdWithCommaSeparator } from "@/utility/string.utility";
import { legacyUrlString } from "@/utility/url";
import WorkSlider from "../work-page/work-slider";
import styles from "./work-added-to-cart-drawer.module.scss";

const FREE_SHIPPING_DETAIL_ITEMS = [
  "Certain items are not eligible based on their size. Items that are not eligible will be noted on their product page and in the cart.",
  "Cancelling items may affect your order's eligibility for Free Shipping.",
];

interface WorkAddedToCartDrawerProps {
  isOpen: boolean;
  cart: LegacyCartResponse | null;
  item: LegacyCartItem | null;
  quantity: number;
  onClose: () => void;
}

interface ShippingMessage {
  content: ReactNode;
  showDetails: boolean;
}

const getShippingThreshold = (cart: LegacyCartResponse): number =>
  cart.FreeShippingThreshold > 0
    ? cart.FreeShippingThreshold
    : DEFAULT_FREE_SHIPPING_THRESHOLD;

const getShippingMessage = (
  cart: LegacyCartResponse,
  item: LegacyCartItem,
): ShippingMessage | null => {
  const threshold = getShippingThreshold(cart);
  const eligibleSubtotal = cart.FreeShippingProgressSubtotal;
  const itemIsEligible = (item.ShippingSurcharge ?? 0) <= 0;
  const amountRemaining = formatUsdWithCommaSeparator(
    Math.max(0, threshold - eligibleSubtotal),
  );

  if (itemIsEligible && eligibleSubtotal >= threshold) {
    if (cart.IsMixedEligibilityCart) {
      return {
        content: (
          <>
            Part of your order qualifies for <strong>FREE</strong> Shipping!
          </>
        ),
        showDetails: true,
      };
    }
    return {
      content: (
        <>
          Plus <strong>FREE</strong> U.S. Shipping on this item!
        </>
      ),
      showDetails: false,
    };
  }

  if (itemIsEligible) {
    return {
      content: (
        <>
          Add <strong>{amountRemaining}</strong> to ship this item FREE!
        </>
      ),
      showDetails: true,
    };
  }

  if (eligibleSubtotal >= threshold) {
    return {
      content: (
        <>
          Part of your order qualifies for <strong>FREE</strong> Shipping!
        </>
      ),
      showDetails: true,
    };
  }

  if (eligibleSubtotal > 0) {
    return {
      content: (
        <>
          Add <strong>{amountRemaining}</strong> to ship part of your order
          FREE!
        </>
      ),
      showDetails: true,
    };
  }

  return null;
};

const WorkAddedToCartDrawer = ({
  isOpen: open,
  cart,
  item,
  quantity,
  onClose,
}: WorkAddedToCartDrawerProps) => {
  const [showShippingDetails, setShowShippingDetails] = useState(false);
  const [recommendationSlider, setRecommendationSlider] =
    useState<SliderRecommendations>();

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    getWorkDrawerRecentBrowsingSlider()
      .then((slider) => {
        if (!cancelled) setRecommendationSlider(slider);
      })
      .catch((error) =>
        logError(error, {
          context: "WorkAddedToCartDrawer:recentBrowsing",
        }),
      );

    return () => {
      cancelled = true;
    };
  }, [open]);
  const controlShippingMessage =
    cart && item ? getShippingMessage(cart, item) : null;
  const isShippingVariant = cart?.AbTestVariant?.toLowerCase() === "variant";
  const shippingThreshold = cart
    ? getShippingThreshold(cart)
    : DEFAULT_FREE_SHIPPING_THRESHOLD;
  const shouldShowProgress = Boolean(
    isShippingVariant && cart?.HasEligibleFreeShippingItems,
  );
  const mixedEligibilityMessageIsVisible = Boolean(
    isShippingVariant &&
    cart?.IsMixedEligibilityCart &&
    cart.FreeShippingProgressSubtotal >= shippingThreshold,
  );
  let shippingMessage = isShippingVariant ? null : controlShippingMessage;
  if (mixedEligibilityMessageIsVisible) {
    shippingMessage = {
      content: (
        <>
          Part of your order qualifies for <strong>FREE</strong> Shipping!
        </>
      ),
      showDetails: true,
    };
  }

  const handleClose = () => {
    setShowShippingDetails(false);
    setRecommendationSlider(undefined);
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleClose();
      }}
      paperClassName={styles.paper}
    >
      <div
        className={styles.root}
        role="dialog"
        aria-modal="true"
        aria-labelledby="work-added-to-cart-title"
      >
        <header className={styles.header}>
          <h2 id="work-added-to-cart-title">
            <CheckIcon aria-hidden />
            Added to your cart
          </h2>
          <IconButton
            ariaLabel="Close added to cart"
            className={styles.closeButton}
            onClick={handleClose}
          >
            <CloseIcon aria-hidden />
          </IconButton>
        </header>

        {cart && item && (
          <div className={styles.content}>
            {(shippingMessage || shouldShowProgress) && (
              <section
                className={styles.shipping}
                aria-label="Shipping eligibility"
              >
                <div className={styles.shippingMessage}>
                  <TruckIcon aria-hidden />
                  <span>
                    {shippingMessage?.content}
                    {shippingMessage?.showDetails && (
                      <Button
                        text={showShippingDetails ? "Hide details" : "Details"}
                        variant="link"
                        size="small"
                        onClick={() =>
                          setShowShippingDetails((current) => !current)
                        }
                      />
                    )}
                  </span>
                </div>
                {shouldShowProgress && (
                  <FreeShippingProgress
                    className={styles.shippingProgress}
                    currentSubtotal={cart.FreeShippingProgressSubtotal}
                    shippingHurdle={shippingThreshold}
                  />
                )}
                {showShippingDetails && (
                  <div className={styles.shippingDetails}>
                    <strong>Free Standard Shipping Eligibility</strong>
                    <ul>
                      <li>
                        Offer applies to orders of eligible items shipping
                        within the U.S. totaling{" "}
                        {formatUsdWithCommaSeparator(shippingThreshold)} and
                        over (excluding taxes).
                      </li>
                      {FREE_SHIPPING_DETAIL_ITEMS.map((detail) => (
                        <li key={detail}>{detail}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </section>
            )}

            <section className={styles.item} aria-label="Item added">
              <Image
                src={item.MediumImageUrl || noImagePlaceholderLarge}
                alt=""
                width={88}
                height={132}
                sizes="88px"
              />
              <div className={styles.itemDetails}>
                <strong className={styles.itemTitle}>{item.Title}</strong>
                {item.Author && <span>By {item.Author}</span>}
                {(item.Format || item.Condition) && (
                  <span>
                    {[item.Format, item.Condition].filter(Boolean).join(" / ")}
                  </span>
                )}
                <span>Qty: {quantity}</span>
                <span>{formatUsdWithCommaSeparator(item.Price)} each</span>
              </div>
            </section>

            <div className={styles.subtotal}>
              <span>
                Cart Subtotal ({cart.CartTotalItemQuantity}{" "}
                {cart.CartTotalItemQuantity === 1 ? "item" : "items"}):
              </span>
              <strong>{formatUsdWithCommaSeparator(cart.Total)}</strong>
            </div>

            <div className={styles.actions}>
              <Button
                text="Continue Shopping"
                fullWidth
                emphasis
                rounded
                onClick={handleClose}
              />
              <Button
                text="View Cart & Checkout"
                href={legacyUrlString("/shopping-cart/")}
                variant="outlined"
                fullWidth
                rounded
              />
            </div>

            {recommendationSlider && (
              <div className={styles.recommendations}>
                <WorkSlider slider={recommendationSlider} compact />
              </div>
            )}
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default WorkAddedToCartDrawer;
